import { useNavigate } from 'react-router-dom';

export default function WebhooksDocs() {
  const navigate = useNavigate();

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', color: '#1A1A1A' }}>
      <div style={{ padding: '2rem', maxWidth: '780px', margin: '0 auto', lineHeight: 1.6 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>← Back</button>

        <h1 style={{ fontSize: '1.6rem', fontWeight: 500, margin: '0 0 0.5rem' }}>Webhooks</h1>
        <p style={{ color: '#666', margin: '0 0 2rem' }}>Receive real-time events from Client Engine in your own application, automation platform (Zapier, Make, n8n), or backend.</p>

        <h2 style={h2}>Overview</h2>
        <p>Webhooks send a JSON HTTP <code style={code}>POST</code> to a URL you control whenever an event happens. Events are scoped to a single client workspace. You configure them at <code style={code}>/clients/&lt;id&gt;/webhooks</code> or via API.</p>

        <h2 style={h2}>Quick start</h2>
        <ol style={ol}>
          <li>Open <strong>Webhooks</strong> in your client workspace.</li>
          <li>Click <strong>+ New webhook</strong>, paste your receiver URL, tick the events you want, save.</li>
          <li>Copy the <strong>signing secret</strong> shown after creation. It is shown once.</li>
          <li>Click <strong>Test</strong> on the new row to send a sample event.</li>
        </ol>

        <h2 style={h2}>Payload structure</h2>
        <p>Every event is a JSON body with this envelope:</p>
        <pre style={pre}>{`{
  "id": "wh_evt_a8f29b…",
  "event": "sequence.approved",
  "client_id": 42,
  "created_at": "2026-05-19T14:08:21.142Z",
  "data": {
    "sequence_id": 1841,
    "contact_id": 9020,
    "contact_name": "Jane Doe",
    "contact_email": "jane@example.com",
    "company_id": 312,
    "company_name": "Example Ltd",
    "channels": ["email", "linkedin"],
    "approved_at": "2026-05-19T14:08:20.998Z"
  }
}`}</pre>

        <h2 style={h2}>Headers</h2>
        <ul style={ul}>
          <li><code style={code}>Content-Type: application/json</code></li>
          <li><code style={code}>X-CE-Event</code> — the event name (e.g. <code style={code}>sequence.approved</code>)</li>
          <li><code style={code}>X-CE-Delivery</code> — unique delivery UUID (use this for idempotency)</li>
          <li><code style={code}>X-CE-Signature</code> — HMAC-SHA256 of the raw body, prefixed <code style={code}>sha256=</code></li>
          <li><code style={code}>X-CE-Timestamp</code> — Unix epoch seconds when the event was generated</li>
        </ul>

        <h2 style={h2}>Verifying the signature</h2>
        <p>Reject any request whose signature you cannot verify. Compute HMAC-SHA256 over the raw request body using the signing secret, then constant-time compare to the value in <code style={code}>X-CE-Signature</code> (without the <code style={code}>sha256=</code> prefix).</p>
        <p style={{ marginTop: '1rem', fontWeight: 500 }}>Node.js</p>
        <pre style={pre}>{`const crypto = require('crypto');

function verify(req, secret) {
  const sigHeader = req.get('X-CE-Signature') || '';
  const sent = sigHeader.replace(/^sha256=/, '');
  const expected = crypto.createHmac('sha256', secret)
    .update(req.rawBody)            // raw bytes, not parsed JSON
    .digest('hex');
  return crypto.timingSafeEqual(Buffer.from(sent), Buffer.from(expected));
}`}</pre>
        <p style={{ marginTop: '1rem', fontWeight: 500 }}>Python</p>
        <pre style={pre}>{`import hmac, hashlib

def verify(request, secret):
    sig = request.headers.get('X-CE-Signature', '').removeprefix('sha256=')
    expected = hmac.new(secret.encode(), request.body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(sig, expected)`}</pre>

        <h2 style={h2}>Retries</h2>
        <p>If your endpoint returns a non-2xx response or times out, Client Engine retries up to 6 times after the first attempt, with backoff:</p>
        <ul style={ul}>
          <li>30 seconds</li>
          <li>2 minutes</li>
          <li>10 minutes</li>
          <li>1 hour</li>
          <li>6 hours</li>
          <li>8 hours</li>
        </ul>
        <p>After 7 total failed attempts on consecutive deliveries, the webhook is auto-disabled and the workspace admin is notified by email. You can re-enable it from the Webhooks settings page.</p>

        <h2 style={h2}>Idempotency</h2>
        <p><code style={code}>X-CE-Delivery</code> is unique per delivery attempt. Use it as a deduplication key. If you receive the same delivery UUID twice, treat the second as a no-op.</p>

        <h2 style={h2}>Event catalogue</h2>
        <table style={tbl}>
          <thead>
            <tr><th style={th}>Event</th><th style={th}>When it fires</th></tr>
          </thead>
          <tbody>
            <tr><td style={td}><code style={code}>sequence.approved</code></td><td style={td}>A sequence is approved for sending.</td></tr>
            <tr><td style={td}><code style={code}>sequence.pushed</code></td><td style={td}>A sequence is pushed to ManyReach or another sender.</td></tr>
            <tr><td style={td}><code style={code}>contact.enriched</code></td><td style={td}>A contact is enriched (standard or deep).</td></tr>
            <tr><td style={td}><code style={code}>contact.scored</code></td><td style={td}>A contact’s prospect score is calculated.</td></tr>
            <tr><td style={td}><code style={code}>contact.added</code></td><td style={td}>A contact is manually added to the workspace.</td></tr>
            <tr><td style={td}><code style={code}>signal.detected</code></td><td style={td}>A new buying signal is detected for a company.</td></tr>
            <tr><td style={td}><code style={code}>alert.created</code></td><td style={td}>A high-relevance signal triggers an alert.</td></tr>
            <tr><td style={td}><code style={code}>company.added</code></td><td style={td}>A company is added to the workspace.</td></tr>
            <tr><td style={td}><code style={code}>companies.imported</code></td><td style={td}>A bulk company import (CSV, ICP search, find-similar) completes.</td></tr>
            <tr><td style={td}><code style={code}>contacts.imported</code></td><td style={td}>A bulk contact CSV import completes.</td></tr>
            <tr><td style={td}><code style={code}>job_change.detected</code></td><td style={td}>A tracked contact has moved to a new role.</td></tr>
          </tbody>
        </table>

        <h2 style={h2}>Using with Zapier or Make</h2>
        <p>Use a generic <strong>Webhooks by Zapier</strong> or Make <strong>Custom Webhook</strong> trigger. Paste the catch URL into Client Engine. Test the webhook from the Client Engine UI to populate the sample payload in your automation builder.</p>
        <p>Signature verification is optional in Zapier/Make but recommended for production. Both platforms can run a small filter or code step that recomputes the HMAC and rejects mismatched events.</p>

        <h2 style={h2}>Troubleshooting</h2>
        <ul style={ul}>
          <li><strong>Deliveries shown as failed:</strong> open the webhook’s Deliveries log to see the response status and body. Most issues are 4xx from your receiver, often missing auth or wrong route.</li>
          <li><strong>Auto-disabled webhook:</strong> fix the receiver, then click Re-enable in the Webhooks settings.</li>
          <li><strong>Signature mismatch:</strong> ensure you are hashing the <em>raw</em> request body, not the parsed JSON. Many frameworks rebuild the body which changes byte order.</li>
        </ul>
      </div>
    </div>
  );
}

const h2 = { fontSize: '1.05rem', fontWeight: 500, margin: '2rem 0 0.6rem', color: '#1A1A1A' };
const code = { fontFamily: 'monospace', background: '#F1EEEA', padding: '0.1rem 0.35rem', borderRadius: '4px', fontSize: '0.85em' };
const pre = { fontFamily: 'monospace', background: '#1A1A1A', color: '#F5F5F0', padding: '0.9rem 1rem', borderRadius: '8px', fontSize: '0.78rem', overflow: 'auto', lineHeight: 1.5 };
const ol = { paddingLeft: '1.25rem', margin: '0 0 1rem' };
const ul = { paddingLeft: '1.25rem', margin: '0 0 1rem' };
const tbl = { width: '100%', borderCollapse: 'collapse', marginBottom: '1rem', fontSize: '0.85rem' };
const th = { textAlign: 'left', borderBottom: '0.5px solid #D4CFC9', padding: '0.5rem 0.6rem', fontWeight: 500 };
const td = { borderBottom: '0.5px solid #E8E4E0', padding: '0.5rem 0.6rem', verticalAlign: 'top' };
